import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Lock, KeyRound } from "lucide-react";
import { api, LOGO_URL } from "../lib/config";

export default function AdminLoginPage() {
  const nav = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [totp, setTotp] = useState("");
  const [need2fa, setNeed2fa] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("sessione") === "scaduta") {
      toast.info("Sessione scaduta: accedi di nuovo");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/admin/login", { ...form, totp_code: need2fa ? totp : undefined });
      if (r.data.requires_2fa) {
        setNeed2fa(true);
        toast.info("Inserisci il codice 2FA");
        setLoading(false);
        return;
      }
      localStorage.setItem("fp_admin_token", r.data.token);
      localStorage.setItem("fp_admin_user", r.data.username);
      toast.success("Accesso effettuato");
      nav("/admin/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Credenziali non valide");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-fp-bg grid-bg flex items-center justify-center p-5" data-testid="admin-login-page">
      <div className="absolute inset-0 radial-glow opacity-30" />
      <form onSubmit={submit} className="relative w-full max-w-md p-8 md:p-10 rounded-3xl border border-white/10 bg-fp-surface/70 backdrop-blur-xl tracing-border">
        <div className="flex items-center gap-3 mb-6">
          <img src={LOGO_URL} alt="" className="w-12 h-12" />
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">Admin</div>
            <h1 className="font-heading font-black text-2xl silver-gradient">Accedi al pannello</h1>
          </div>
        </div>

        {!need2fa && (
          <div className="space-y-4">
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">Username</label>
              <input type="text" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="mt-2 w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan focus:outline-none text-white"
                autoComplete="username" data-testid="admin-username" />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">Password</label>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" size={16} />
                <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full pl-11 pr-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan focus:outline-none text-white"
                  autoComplete="current-password" data-testid="admin-password" />
              </div>
            </div>
          </div>
        )}

        {need2fa && (
          <div data-testid="admin-2fa-step">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-fp-cyan/10 border border-fp-cyan/30 text-fp-cyan flex items-center justify-center">
                <KeyRound size={20} />
              </div>
              <div>
                <div className="font-heading font-bold">Autenticazione a due fattori</div>
                <div className="text-xs text-white/50">Apri l'app authenticator e inserisci il codice</div>
              </div>
            </div>
            <input type="text" inputMode="numeric" maxLength={8} value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
              className="w-full px-4 py-4 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan focus:outline-none text-white text-center text-3xl font-heading font-black tracking-[0.4em]"
              placeholder="• • • • • •" data-testid="admin-totp" autoFocus />
            <button type="button" onClick={() => { setNeed2fa(false); setTotp(""); }} className="mt-4 text-xs text-white/50 hover:text-fp-cyan">
              ← Torna indietro
            </button>
          </div>
        )}

        <button type="submit" disabled={loading || (need2fa && totp.length < 6)}
          className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold tracking-wider uppercase text-sm hover:shadow-[0_0_25px_rgba(0,229,255,0.6)] hover:-translate-y-0.5 transition-all disabled:opacity-50"
          data-testid="admin-login-btn">
          <Shield size={16} /> {loading ? "Accesso..." : need2fa ? "Verifica codice" : "Entra"}
        </button>

        <Link to="/" className="mt-6 block text-center text-xs text-white/40 hover:text-fp-cyan">← Torna al sito</Link>
      </form>
    </div>
  );
}
